import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  InvoiceCompanyClient,
  InvoiceNextDraftNumber,
  InvoicePrivateClient,
  NewInvoice,
  TableInvoiceFields,
  TableReceivedInvoiceFields,
} from '../models/invoices';
import { ExpenseCreateResponse } from '../models/api/responses/invoices-sts.response';
import {
  StsCreditNotePayload,
  StsExpense,
  toStsCreditNoteApiBody,
} from '../pages/invoices-page/invoices-sts/sts-models';

@Injectable({
  providedIn: 'root',
})
export class InvoicesService {
  private apiUrl = environment.apiUrl;
  constructor(private http: HttpClient) {}

  addHeader(): HttpHeaders {
    const token = sessionStorage.getItem('psicogeniusAccessToken');

    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  getInvoicesCompanyClients(): Observable<InvoiceCompanyClient[]> {
    const endpoint = this.apiUrl + '/invoicing/company-clients';
    const headers = this.addHeader();
    return this.http.get<InvoiceCompanyClient[]>(endpoint, { headers });
  }

  createInvoicesCompanyClients(data: any): Observable<any> {
    const endpoint = this.apiUrl + '/invoicing/company-clients';
    const headers = this.addHeader();
    return this.http.post(endpoint, data, { headers });
  }

  deleteInvoicesCompanyClients(clientId: string): Observable<any> {
    const endpoint = this.apiUrl + '/invoicing/company-clients/' + clientId;
    const headers = this.addHeader();
    return this.http.delete(endpoint, { headers });
  }

  updateInvoicesCompanyClients(clientId: string, data: any): Observable<any> {
    const endpoint = this.apiUrl + '/invoicing/company-clients/' + clientId;
    const headers = this.addHeader();
    return this.http.put(endpoint, data, { headers });
  }

  getCompanyClient(clientId: string): Observable<InvoiceCompanyClient> {
    const endpoint = this.apiUrl + '/invoicing/company-clients/' + clientId;
    const headers = this.addHeader();
    return this.http.get<InvoiceCompanyClient>(endpoint, { headers });
  }

  getInvoicesPrivateClients(): Observable<InvoicePrivateClient[]> {
    const endpoint = this.apiUrl + '/invoicing/private-clients';
    const headers = this.addHeader();
    return this.http.get<InvoicePrivateClient[]>(endpoint, { headers });
  }

  createInvoicesPrivateClients(data: any): Observable<any> {
    const endpoint = this.apiUrl + '/invoicing/private-clients';
    const headers = this.addHeader();
    return this.http.post(endpoint, data, { headers });
  }

  getPrivateClient(clientId: string): Observable<InvoicePrivateClient> {
    const endpoint = this.apiUrl + '/invoicing/private-clients/' + clientId;
    const headers = this.addHeader();
    return this.http.get<InvoicePrivateClient>(endpoint, { headers });
  }

  updateInvoicesPrivateClients(clientId: string, data: any): Observable<any> {
    const endpoint = this.apiUrl + '/invoicing/private-clients/' + clientId;
    const headers = this.addHeader();
    return this.http.put(endpoint, data, { headers });
  }

  deleteInvoicesPrivateClients(clientId: string): Observable<any> {
    const endpoint = this.apiUrl + '/invoicing/private-clients/' + clientId;
    const headers = this.addHeader();
    return this.http.delete(endpoint, { headers });
  }

  // API FOR INVOICES STS

  getSTSInvoices(): Observable<TableInvoiceFields[]> {
    const endpoint = this.apiUrl + '/invoicing/sts';
    const headers = this.addHeader();
    return this.http.get<TableInvoiceFields[]>(endpoint, { headers });
  }

  getSTSInvoiceById(invoiceId: string): Observable<StsExpense> {
    const endpoint = this.apiUrl + '/invoicing/sts/' + invoiceId;
    const headers = this.addHeader();
    return this.http.get<StsExpense>(endpoint, { headers });
  }

  createInvoiceSTSDraft(data: any): Observable<any> {
    const endpoint = this.apiUrl + '/invoicing/sts/draft';
    const headers = this.addHeader();
    return this.http.post(endpoint, data, { headers });
  }

  modifyInvoiceSTSDraft(invoiceId: string, data: any): Observable<any> {
    const endpoint = this.apiUrl + '/invoicing/sts/draft/' + invoiceId;
    const headers = this.addHeader();
    return this.http.put(endpoint, data, { headers });
  }

  deleteInvoiceSTSDraft(invoiceId: string): Observable<any> {
    const endpoint = this.apiUrl + '/invoicing/sts/draft/' + invoiceId;
    const headers = this.addHeader();
    return this.http.delete(endpoint, { headers });
  }

  deleteInvoiceSTS(invoiceId: string): Observable<any> {
    const endpoint = this.apiUrl + '/invoicing/sts/' + invoiceId;
    const headers = this.addHeader();
    return this.http.delete(endpoint, { headers });
  }

  promoteDraftInvoiceSTSToReady(invoiceId: string): Observable<any> {
    const endpoint = `${this.apiUrl}/invoicing/sts/${invoiceId}/transitions/ready`;

    const headers = this.addHeader();
    return this.http.post(endpoint, {}, { headers });
  }

  getSTSNextNumber(year?: string): Observable<InvoiceNextDraftNumber> {
    const endpoint =
      this.apiUrl +
      '/invoicing/sts/draft/next-number' +
      (year ? '?year=' + year : '');
    const headers = this.addHeader();
    return this.http.get<InvoiceNextDraftNumber>(endpoint, { headers });
  }

  sendSTSInvoice(invoiceId: string): Observable<ExpenseCreateResponse> {
    const endpoint =
      this.apiUrl + '/invoicing/sts/' + invoiceId + '/transitions/send';
    const headers = this.addHeader();
    return this.http.post(endpoint, {}, { headers });
  }

  sendSTSCreditNote(
    data: StsCreditNotePayload,
    invoiceId: string
  ): Observable<any> {
    const endpoint =
      this.apiUrl + '/invoicing/sts/' + invoiceId + '/send-credit-note';
    const headers = this.addHeader();
    return this.http.post(endpoint, toStsCreditNoteApiBody(data), { headers });
  }

  modifyInvoiceSTSPagata(invoiceId: string, data: any): Observable<any> {
    const endpoint = this.apiUrl + '/invoicing/sts/update-pagata/' + invoiceId;
    const headers = this.addHeader();
    return this.http.put(endpoint, data, { headers });
  }

  downloadInvoiceSTSPDF(invoiceId: string): Observable<Blob> {
    const endpoint = this.apiUrl + '/invoicing/sts/' + invoiceId + '/pdf';
    const headers = this.addHeader();
    return this.http.get(endpoint, {
      headers,
      responseType: 'blob',
    });
  }

  revertInvoiceSTSToBozza(invoiceId: string): Observable<any> {
    const endpoint = `${this.apiUrl}/invoicing/sts/${invoiceId}/transitions/draft`;

    const headers = this.addHeader();
    return this.http.post(endpoint, {}, { headers });
  }

  // API FOR INVOICES SDI

  getSDIInvoices(): Observable<TableInvoiceFields[]> {
    const endpoint = this.apiUrl + '/invoicing/sdi';
    const headers = this.addHeader();
    return this.http.get<TableInvoiceFields[]>(endpoint, { headers });
  }

  getSDIInvoiceById(invoiceId: string): Observable<NewInvoice> {
    const endpoint = this.apiUrl + '/invoicing/sdi/' + invoiceId;
    const headers = this.addHeader();
    return this.http.get<NewInvoice>(endpoint, { headers });
  }

  createInvoiceSDIDraft(data: any): Observable<any> {
    const endpoint = this.apiUrl + '/invoicing/sdi/draft';
    const headers = this.addHeader();
    return this.http.post(endpoint, data, { headers });
  }

  modifyInvoiceSDIDraft(invoiceId: string, data: any): Observable<any> {
    const endpoint = this.apiUrl + '/invoicing/sdi/draft/' + invoiceId;
    const headers = this.addHeader();
    return this.http.put(endpoint, data, { headers });
  }

  modifyInvoiceSDIPagata(invoiceId: string, data: any): Observable<any> {
    const endpoint = this.apiUrl + '/invoicing/sdi/update-pagata/' + invoiceId;
    const headers = this.addHeader();
    return this.http.put(endpoint, data, { headers });
  }

  deleteInvoiceSDIDraft(invoiceId: string): Observable<any> {
    const endpoint = this.apiUrl + '/invoicing/sdi/draft/' + invoiceId;
    const headers = this.addHeader();
    return this.http.delete(endpoint, { headers });
  }

  createInvoiceSDIReady(data: any): Observable<any> {
    const endpoint = this.apiUrl + '/invoicing/sdi/ready';
    const headers = this.addHeader();
    return this.http.post(endpoint, data, { headers });
  }

  promoteDraftInvoiceSDIToReady(invoiceId: string, data: any): Observable<any> {
    const endpoint = this.apiUrl + '/invoicing/sdi/ready/' + invoiceId;
    const headers = this.addHeader();
    return this.http.put(endpoint, data, { headers });
  }

  getSDINextNumber(year?: string): Observable<InvoiceNextDraftNumber> {
    const endpoint =
      this.apiUrl +
      '/invoicing/sdi/draft/next-number' +
      (year ? '?year=' + year : '');
    const headers = this.addHeader();
    return this.http.get<InvoiceNextDraftNumber>(endpoint, { headers });
  }

  sendSDIInvoice(invoiceId: string): Observable<any> {
    const endpoint =
      this.apiUrl + '/invoicing/sdi/send?invoice_id=' + invoiceId;
    const headers = this.addHeader();
    return this.http.post(endpoint, {}, { headers });
  }

  getReceivedInvoices(): Observable<TableReceivedInvoiceFields[]> {
    const endpoint = this.apiUrl + '/invoicing/sdi/received';
    const headers = this.addHeader();
    return this.http.get<TableReceivedInvoiceFields[]>(endpoint, { headers });
  }

  setCodiceUnivocoRegisteredInADEFlag(value: boolean): Observable<any> {
    const endpoint =
      this.apiUrl + '/invoicing/sdi/codice-univoco-registered-in-ade-flag';
    const headers = this.addHeader();
    return this.http.put(endpoint, { value: value }, { headers });
  }

  getCodiceUnivocoRegisteredInADEFlag(): Observable<boolean> {
    const endpoint =
      this.apiUrl + '/invoicing/sdi/codice-univoco-registered-in-ade-flag';
    const headers = this.addHeader();
    return this.http.get<boolean>(endpoint, { headers });
  }

  validateDoctorBillingData(): Observable<any> {
    const endpoint = this.apiUrl + '/invoicing/validate-doctor-fields';
    const headers = this.addHeader();
    return this.http.get(endpoint, { headers });
  }

  downloadInvoiceSDIPDF(invoiceId: string): Observable<Blob> {
    const endpoint = this.apiUrl + '/invoicing/sdi/' + invoiceId + '/pdf';
    const headers = this.addHeader();
    return this.http.get(endpoint, {
      headers,
      responseType: 'blob',
    });
  }

  getReceipt(invoiceId: string): Observable<Blob> {
    const endpoint = this.apiUrl + '/invoicing/sts/' + invoiceId + '/receipt';
    const headers = this.addHeader();
    return this.http.get(endpoint, {
      headers,
      responseType: 'blob',
    });
  }

  retrocedeInvoiceSDIToBozza(invoiceId: string): Observable<any> {
    const endpoint =
      this.apiUrl + '/invoicing/sdi/retrocede-to-draft/' + invoiceId;
    const headers = this.addHeader();
    return this.http.put(endpoint, {}, { headers });
  }
}
